import React, { useState, useEffect, useCallback } from 'react';
import {
  differenceInSeconds,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
  differenceInYears
} from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import _ from 'lodash';

// constants
import LocalStrings from '../../assets/languages';
import {
  INTERVAL_5,
  PORTAL_LOCALSTORAGE_ACCESS_KEY
} from '../../constants/constant';
import { useInterval } from '../../util/util-common';
import { commonDateFNSFormatter } from '../../util/util-common';
import { loadFromLocalDB } from '../../reducers/localStorage';

/**
 *
 * PARAMS DETAILS FOR THIS COMPONENT TO USE:
 * time : [REQUIRED]
 * fns_format : [OPTIONAL] if 'X UNIT AGO' format is not needed
 * session : [OPTIONAL] for session type dateTime only
 * trigger: [OPTIONAL] for for triggering a rerender based on data update
 * convertAgo : [OPTIONAL] if 'X UNIT AGO' format is needed
 * heading: [OPTIONAL] : chat month/day wise section headings
 * indicate: [OPTIONAL] displays only in hh:mm a or dd/MM/yyyy format
 * chat: [OPTIONAL] for chat time only as heading displays the group time
 * showConvert: [OPTIONAL] paired with session prop, this will render session time in local time zone in all cases
 * doNotConvert: [optional] Will not perform any conversion. Simple date render in given format.
 *
 * convertAgo IS VICE-VERSA OF fns_format
 *
 */

const TimeZoneHandler = props => {
  const {
    // COMPONENT PROPS
    time,
    fns_format = '',
    session = false,
    trigger = null,
    convertAgo = false,
    heading = false,
    indicate = false,
    chat = false,
    showConvert = false,
    doNotConvert = false,

    // STATE TO PROPS
    timezoneDetails,
    convertToZone
  } = props;

  if ((typeof time === 'string' || typeof time === 'object') && _.isEmpty(time))
    return <></>;

  const [nowToZone, setNowToZone] = useState(new Date());
  const [timeToZone, setTimeToZone] = useState(new Date());
  const [intervalCounter, setIntervalCounter] = useState(-1);
  const [timeStr, setTimeStr] = useState('');

  const scheduleLocalStorage = loadFromLocalDB(
    PORTAL_LOCALSTORAGE_ACCESS_KEY.schedule
  );
  const userDoesNotWantSessionTimeConverted = !scheduleLocalStorage?.userWantsConvertedTime;

  useEffect(() => {
    !doNotConvert && prepareTimeDetails();
  }, [trigger]);

  useInterval(() => {
    if (convertAgo) {
      let counter = intervalCounter;
      prepareTimeDetails();
      setIntervalCounter(counter);
    }
  }, INTERVAL_5);

  const prepareTimeDetails = () => {
    let zonedTimeToUTCtime_local = zonedTimeToUtc(
      time,
      timezoneDetails.eventTimezone
    );

    // ------------ setting current time and acquired time
    let nowUTC_local = new Date();
    let nowToZone_local = utcToZonedTime(
      nowUTC_local,
      timezoneDetails.clientTimezone
    ); // converting current utc time to local time forcefully
    // const timeUTC = new Date(`${time} UTC`); // INCORRECT FORMATTING, DOES NOT SUPPORT SAFARI
    // const timeUTC = new Date(`${time}Z`); // // INCORRECT FORMATTING, DOES NOT SUPPORT SAFARI

    // CORRECT FORMAT yyyy-mm-ddThh:mm:ssZ -> splitting by space and joining with T, appending Z to specify the zone property
    let timeStr_local = !_.isEmpty(time)
      ? `${time.split(' ').join('T')}${!session ? 'Z' : ''}`
      : '';

    // const timeToZone = utcToZonedTime(timeUTC, timezoneDetails.clientTimezone);
    let timeToZone_local = utcToZonedTime(
      timeStr_local,
      timezoneDetails.clientTimezone
    ); // converting utc to zone

    setNowToZone(nowToZone_local);
    setTimeToZone(timeToZone_local);
    setTimeStr(timeStr_local);
  };

  // ------------ checking for 'at' in format
  const regex = new RegExp(' at ');
  const formatHasRegex = regex.test(fns_format) ? true : false;

  // -------------- maintains any special date-fns format
  const handleAnySpecialDateFNSFormat = useCallback(() => {
    // assumed format Thursday, 25th April at 6:04 PM
    // assumed provided format E, MMM dd at h:mm a
    let formatString = fns_format;
    let wordsSeperatedBySpace = formatString.split(' ');
    // ['E,', 'MMM', 'dd', 'at', 'h:mm', 'a']
    let formatObj = {
      start: '',
      end: ''
    };

    // looping each word
    !_.isEmpty(wordsSeperatedBySpace) &&
      _.map(wordsSeperatedBySpace, (word, key) => {
        // getting words before at
        if (key < wordsSeperatedBySpace.indexOf('at')) {
          formatObj.start = `${formatObj.start} ${word}`.trim();
        }

        // getting words after at
        if (key > wordsSeperatedBySpace.indexOf('at')) {
          formatObj.end = `${formatObj.end} ${word}`.trim();
        }
      });

    return (
      <>
        <span className="">
          {commonDateFNSFormatter(timeToZone, formatObj.start)}
          &nbsp;{LocalStrings.dynamic.kAt}&nbsp;
          {commonDateFNSFormatter(timeToZone, formatObj.end)}
        </span>
      </>
    );
  }, [intervalCounter, fns_format, timeToZone, LocalStrings, time]);

  // -------------- maintains the convertAgo format
  const convertTimeToAgoFormat = useCallback(() => {
    if (!timeToZone || !nowToZone) return '';

    // -------------- calculating diff in times
    let diffInSeconds = differenceInSeconds(nowToZone, timeToZone);
    let diffInMinutes = differenceInMinutes(nowToZone, timeToZone);
    let diffInHours = differenceInHours(nowToZone, timeToZone);
    let diffInDays = differenceInDays(nowToZone, timeToZone);
    let diffInWeeks = differenceInWeeks(nowToZone, timeToZone);
    let diffInMonths = differenceInMonths(nowToZone, timeToZone);
    let diffInYears = differenceInYears(nowToZone, timeToZone);

    // ------------- acting according to time
    // < 1 minute = "Just now"
    if (diffInSeconds < 60) {
      return indicate ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'p')}</span>
      ) : heading ? (
        <span className="">{LocalStrings.dynamic.kTodayTxt}</span>
      ) : (
        <span className="">{_.startCase(LocalStrings.dynamic.kJustNow)}</span>
      );
    }

    // < 1 hour = "x minutes ago"
    if (diffInMinutes == 1) {
      return indicate ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'p')}</span>
      ) : heading ? (
        <span className="">{LocalStrings.dynamic.kTodayTxt}</span>
      ) : chat ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'p')}</span>
      ) : (
        <span className="">
          {diffInMinutes}&nbsp;{LocalStrings.dynamic.kMinuteAgo}
        </span>
      );
    }
    if (diffInMinutes > 0 && diffInMinutes < 59) {
      return indicate ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'p')}</span>
      ) : heading ? (
        <span className="">{LocalStrings.dynamic.kTodayTxt}</span>
      ) : chat ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'p')}</span>
      ) : (
        <span className="">
          {diffInMinutes}&nbsp;{LocalStrings.dynamic.kMinutesAgo}
        </span>
      );
    }

    // Today = "x hours ago"
    if (diffInHours == 1) {
      return indicate ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'p')}</span>
      ) : heading ? (
        <span className="">{LocalStrings.dynamic.kTodayTxt}</span>
      ) : chat ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'p')}</span>
      ) : (
        <span className="">
          {diffInHours}&nbsp;{LocalStrings.dynamic.kHourAgo}
        </span>
      );
    }
    if (diffInHours > 0 && diffInHours < 24) {
      return indicate ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'p')}</span>
      ) : heading ? (
        <span className="">{LocalStrings.dynamic.kTodayTxt}</span>
      ) : chat ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'p')}</span>
      ) : (
        <span className="">
          {diffInHours}&nbsp;{LocalStrings.dynamic.kHoursAgo}
        </span>
      );
    }

    // Yesterday = "Yesterday"
    if (diffInDays == 1) {
      return indicate ? (
        <span className="">{LocalStrings.dynamic.kYesterdayTxt}</span>
      ) : heading ? (
        <span className="">{LocalStrings.dynamic.kYesterdayTxt}</span>
      ) : chat ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'p')}</span>
      ) : (
        <span className="">
          {/* {LocalStrings.dynamic.kYesterdayTxt} */}
          {LocalStrings.dynamic.kYesterdayTxt}&nbsp;
          {commonDateFNSFormatter(timeToZone, 'p')}
        </span>
      );
    }

    // < Last 7 days = "Friday"
    if (diffInDays < 7) {
      return indicate ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'P')}</span>
      ) : heading ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'EEEE')}</span>
      ) : chat ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'p')}</span>
      ) : (
        <span className="">
          {commonDateFNSFormatter(timeToZone, 'EEEE')},&nbsp;
          {commonDateFNSFormatter(timeToZone, 'p')}
        </span>
      );
    }

    // < Last 30 days = "March 30"
    if (diffInDays < 30) {
      return indicate ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'P')}</span>
      ) : heading ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'MMMM d')}</span>
      ) : chat ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'p')}</span>
      ) : (
        <span className="">
          {commonDateFNSFormatter(timeToZone, 'MMMM d')},&nbsp;
          {commonDateFNSFormatter(timeToZone, 'p')}
        </span>
      );
    }

    // < 1 year = "September 15" --> Changes as per notification time format
    if (diffInMonths > 0 && diffInYears < 1) {
      return indicate ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'P')}</span>
      ) : heading ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'MMMM d')}</span>
      ) : chat ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'p')}</span>
      ) : (
        <span className="">
          {commonDateFNSFormatter(timeToZone, 'MMMM d')},&nbsp;
          {commonDateFNSFormatter(timeToZone, 'p')}
        </span>
      );
    }

    // Anything else = "September 9, 2011"
    if (diffInYears >= 1) {
      return indicate ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'P')}</span>
      ) : heading ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'PP')}</span>
      ) : chat ? (
        <span className="">{commonDateFNSFormatter(timeToZone, 'p')}</span>
      ) : (
        <span className="">{commonDateFNSFormatter(timeToZone, 'PPPp')}</span>
      );
    }

    return indicate ? (
      <span className="">{commonDateFNSFormatter(timeToZone, 'P')}</span>
    ) : heading ? (
      <span className="">{commonDateFNSFormatter(timeToZone, 'PP')}</span>
    ) : chat ? (
      <span className="">{commonDateFNSFormatter(timeToZone, 'p')}</span>
    ) : (
      <span className="">{commonDateFNSFormatter(timeToZone, 'PPPp')}</span>
    );
  }, [
    intervalCounter,
    timeToZone,
    nowToZone,
    indicate,
    heading,
    chat,
    LocalStrings,
    time
  ]);

  const _renderSessionTime = useCallback(() => {
    // NOTE: If logic updates, also update same logic in session details > handleRateButtonClick, which shows warning about the rating opening time.

    let zonedTimeToUTCtime_local = zonedTimeToUtc(
      time,
      timezoneDetails.eventTimezone
    );
    let utcTimeToClientZoneTime_local = utcToZonedTime(
      zonedTimeToUTCtime_local,
      timezoneDetails.clientTimezone
    );

    // CORRECT FORMAT yyyy-mm-ddThh:mm:ssZ -> splitting by space and joining with T
    let time_local = !_.isEmpty(time) ? time.split(' ').join('T') : '';

    return (
      <span>
        {showConvert || userDoesNotWantSessionTimeConverted
          ? commonDateFNSFormatter(utcTimeToClientZoneTime_local, fns_format)
          : time_local !== ''
          ? commonDateFNSFormatter(new Date(time_local), fns_format)
          : ''}
      </span>
    );
  }, [
    timezoneDetails?.showconvertedtimezone,
    fns_format,
    timeStr,
    fns_format,
    time,
    userDoesNotWantSessionTimeConverted,
    trigger
  ]);

  // ----- main component return
  return session ? (
    _renderSessionTime()
  ) : convertAgo ? (
    convertTimeToAgoFormat()
  ) : formatHasRegex ? (
    handleAnySpecialDateFNSFormat()
  ) : (
    <span>
      {commonDateFNSFormatter(
        doNotConvert ? new Date(time) : timeToZone,
        fns_format
      )}
    </span>
  );
};

const mapStateToProps = state => {
  return {
    timezoneDetails: state.timezoneDetails
  };
};

const mapDispatchToProps = dispatch => bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(TimeZoneHandler);
